import assetService, { Asset } from '../services/assetService';

/**
 * Asset Template Resolver
 * Resolves {{asset:uuid}} templates to actual signed URLs
 */

// Cache for asset URLs to avoid repeated API calls
const assetUrlCache = new Map<string, string>();

/**
 * Resolve all {{asset:uuid}} templates in code to actual URLs
 * @param code - The code containing asset templates
 * @param projectId - The project ID to fetch assets from
 * @returns Promise resolving to code with templates replaced by URLs
 */
export async function resolveAssetTemplates(
  code: string,
  projectId: string
): Promise<string> {
  if (!code || !projectId) {
    return code;
  }

  // Find all {{asset:uuid}} patterns
  const assetPattern = /\{\{asset:([a-f0-9-]+)\}\}/g;
  const matches = Array.from(code.matchAll(assetPattern));

  if (matches.length === 0) {
    return code; // No templates to resolve
  }

  // Extract unique asset IDs
  const assetIds = [...new Set(matches.map(match => match[1]))];

  // Fetch URLs for all assets (batch fetch would be ideal, but we'll use cache)
  const assetUrls = new Map<string, string>();

  try {
    // Fetch all assets for the project (this could be optimized with a batch endpoint)
    const assets = await assetService.listAssets(projectId, 'all', false);
    
    // Build URL map
    for (const asset of assets) {
      if (asset.url) {
        assetUrls.set(asset.id, asset.url);
        assetUrlCache.set(asset.id, asset.url); // Update cache
      }
    }

    // Replace all templates with URLs
    let resolvedCode = code;
    for (const assetId of assetIds) {
      const url = assetUrls.get(assetId) || assetUrlCache.get(assetId);
      if (url) {
        const template = `{{asset:${assetId}}}`;
        resolvedCode = resolvedCode.split(template).join(url);
      } else {
        console.warn(`[AssetTemplateResolver] Asset not found: ${assetId}`);
        // Leave template as-is if asset not found
      }
    }

    return resolvedCode;

  } catch (error) {
    console.error('[AssetTemplateResolver] Error resolving asset templates:', error);
    return code; // Return original code on error
  }
}

/**
 * Clear the asset URL cache (useful when assets are updated)
 */
export function clearAssetCache(): void {
  assetUrlCache.clear();
}

/**
 * Extract all asset IDs from code
 * @param code - The code to extract asset IDs from
 * @returns Array of asset IDs found in the code
 */
export function extractAssetIds(code: string): string[] {
  if (!code) return [];
  
  const assetPattern = /\{\{asset:([a-f0-9-]+)\}\}/g;
  const matches = Array.from(code.matchAll(assetPattern));
  return [...new Set(matches.map(match => match[1]))];
}
